import { authClient } from '@/lib/auth/auth-client';
import { getErrorMessage } from '@/lib/error-utils';
import { ErrorLogger } from '@/lib/logger/logger-utils';
import type { User } from 'better-auth/types';
import { create } from 'zustand';
import { createJSONStorage, persist, subscribeWithSelector } from 'zustand/middleware';

const authErrorLogger = new ErrorLogger('auth-store');

// Helper function to initialize user credits
const initializeUserCredits = async (userId: string): Promise<void> => {
  try {
    const response = await fetch('/api/credits/initialize', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId }),
    });

    if (!response.ok) {
      console.warn('Failed to initialize user credits:', await response.text());
    } else {
      const result = await response.json();
      console.log('Credit initialization result:', result);

      if (result.data?.recentlyGrantedBonus && typeof window !== 'undefined') {
        const d = new Date();
        d.setTime(d.getTime() + 60 * 1000); // 1 minute expiry is enough for toast to read it
        document.cookie = `zappingai_welcome_bonus=true; path=/; expires=${d.toUTCString()}`;

        // Dispatch custom event if we want to immediately trigger it without full refresh
        window.dispatchEvent(new Event('welcome-bonus-granted'));
      }
    }
  } catch (error) {
    console.warn('Error initializing user credits:', getErrorMessage(error));
  }
};

interface AuthState {
  // Persistent state
  user:
    | (User & {
        firstName?: string | null;
        lastName?: string | null;
        marketingEmails?: boolean | null;
      })
    | null;
  isAuthenticated: boolean;
  lastUpdated: number;

  // Temporary state
  isLoading: boolean;
  error: string | null;
  isInitialized: boolean;

  // Cache configuration
  cacheExpiry: number;

  // Actions
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setInitialized: (initialized: boolean) => void;
  setError: (error: string) => void;
  clearError: () => void;

  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signUp: (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    phone?: string
  ) => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<void>;

  signInWithGoogle: (callbackURL?: string) => Promise<void>;
  signInWithLinkedIn: (callbackURL?: string) => Promise<void>;
  signInWithMicrosoft: (callbackURL?: string) => Promise<void>;

  updateUser: (data: {
    image?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
  }) => Promise<{
    success: boolean;
    error?: string;
  }>;

  initialize: () => Promise<void>;
  refreshSession: () => Promise<void>;
  clearAuth: () => void;

  // Cache methods
  isCacheValid: () => boolean;
  invalidateCache: () => void;
  setCacheExpiry: (expiry: number) => void;
}

// Keys used by the Sora generator for persistence
const SORA_GENERATOR_STORAGE_KEYS = [
  'sora_generator_prompt',
  'sora_generator_model',
  'sora_generator_aspectRatio',
  'sora_generator_resolution',
  'sora_generator_duration',
  'sora_generator_variations',
  'sora_generator_image',
  'sora_prompt_backup',
];

// Helper to clear generator-related storage and cookies
const clearGeneratorStorage = () => {
  if (typeof window === 'undefined') return;

  // Clear localStorage
  SORA_GENERATOR_STORAGE_KEYS.forEach((key) => {
    localStorage.removeItem(key);
  });

  // Clear cookies that might affect the generator UI/tracking
  const cookiesToClear = ['zappingai_sign_in', 'zappingai_user_email', 'zappingai_new_signup'];
  cookiesToClear.forEach((name) => {
    document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
    // Also try clearing with domain if needed, but usually path=/ is enough for these
  });

  // Force UI update if there are listeners
  window.dispatchEvent(new Event('storage'));
  window.dispatchEvent(new Event('credit-update'));
};

export const useAuthStore = create<AuthState>()(
  subscribeWithSelector(
    persist(
      (set, get): AuthState => ({
        // Persistent state - default values must match server-side
        user: null,
        isAuthenticated: false,
        lastUpdated: 0,

        // Temporary state
        isLoading: false,
        error: null,
        isInitialized: false,
        cacheExpiry: 10 * 60 * 1000, // 10 minutes

        // Cache methods
        isCacheValid: () => {
          const { lastUpdated, cacheExpiry } = get();
          return lastUpdated > 0 && Date.now() - lastUpdated < cacheExpiry;
        },

        invalidateCache: () => set({ lastUpdated: 0 }),

        setCacheExpiry: (expiry: number) => set({ cacheExpiry: expiry }),

        // Actions
        setUser: (user) => {
          set({
            user,
            isAuthenticated: !!user,
            lastUpdated: Date.now(),
          });
        },

        setLoading: (loading) => {
          set({ isLoading: loading });
        },

        setInitialized: (initialized) => {
          set({ isInitialized: initialized });
        },

        setError: (error) => {
          set({ error });
        },

        clearError: () => {
          set({ error: null });
        },
        signIn: async (email, password) => {
          set({ isLoading: true, error: null });

          try {
            const result = await authClient.signIn.email({
              email,
              password,
            });

            if (result.data) {
              const user = result.data.user;
              set({
                user,
                isAuthenticated: true,
                isLoading: false,
                lastUpdated: Date.now(),
              });
              return { success: true };
            }

            set({ isLoading: false });
            return {
              success: false,
              error: result.error?.message || 'signIn error',
            };
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'signIn error';
            set({ error: errorMessage, isLoading: false });
            return {
              success: false,
              error: errorMessage,
            };
          }
        },
        signUp: async (email, password, firstName, lastName, phone) => {
          set({ isLoading: true, error: null });

          try {
            // Join name for standard display
            const fullName = `${firstName} ${lastName}`.trim();

            const result = await authClient.signUp.email({
              email: email,
              password,
              name: firstName, // Keep for better-auth compatibility (maps to firstName in DB)
              // @ts-ignore - Extra fields passed to better-auth if schema supports them
              lastName,
              phoneNumber: phone,
            });

            if (result.data) {
              const { user, token } = result.data;

              // Only consider authenticated if a token was created
              // (If requireEmailVerification is true, token will be null until verified)
              const hasSession = !!token;

              set({
                user,
                isAuthenticated: hasSession,
                isLoading: false,
                lastUpdated: Date.now(),
              });

              // Only initialize credits if we have an active session (user is verified/logged in)
              if (hasSession) {
                await initializeUserCredits(user.id);
              } else {
                console.log(
                  'User created but verification required - skipping credit initialization until verified.'
                );
              }

              return { success: true, verificationRequired: !hasSession };
            }
            set({ isLoading: false });
            return {
              success: false,
              error: result.error?.message || 'signUp error',
            };
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'signUp error';
            set({ error: errorMessage, isLoading: false });
            return {
              success: false,
              error: errorMessage,
            };
          }
        },

        signOut: async () => {
          set({ isLoading: true });

          try {
            await authClient.signOut();

            // Clear generator storage on sign out
            clearGeneratorStorage();

            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              lastUpdated: 0,
            });
          } catch (error) {
            authErrorLogger.logError(error as Error, {
              operation: 'signOut',
            });

            // Clear generator storage even on error
            clearGeneratorStorage();

            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              lastUpdated: 0,
            });
          }
        },

        signInWithGoogle: async (callbackURL?: string) => {
          set({ isLoading: true, error: null });

          try {
            await authClient.signIn.social({
              provider: 'google',
              callbackURL,
            });

            // Clear cache to force refresh
            set({ lastUpdated: 0 });
          } catch (error) {
            console.error('signInWithGoogle error:', error);
            authErrorLogger.logError(error as Error, {
              operation: 'signInWithGoogle',
            });
            set({ isLoading: false });
          }
        },

        signInWithLinkedIn: async (callbackURL?: string) => {
          set({ isLoading: true, error: null });

          try {
            await authClient.signIn.social({
              provider: 'linkedin',
              callbackURL,
            });
            // Clear cache to force refresh
            set({ lastUpdated: 0 });
          } catch (error) {
            authErrorLogger.logError(error as Error, {
              operation: 'signInWithLinkedIn',
            });
            set({ isLoading: false });
          }
        },

        signInWithMicrosoft: async (callbackURL?: string) => {
          set({ isLoading: true, error: null });

          try {
            await authClient.signIn.social({
              provider: 'microsoft',
              callbackURL,
            });
            // Clear cache to force refresh
            set({ lastUpdated: 0 });
          } catch (error) {
            authErrorLogger.logError(error as Error, {
              operation: 'signInWithMicrosoft',
            });
            set({ isLoading: false });
          }
        },

        updateUser: async (data) => {
          set({ isLoading: true, error: null });

          try {
            const result = await authClient.updateUser(data);

            if (result.data?.status) {
              await get().refreshSession();
              set({ isLoading: false });
              return { success: true };
            }

            set({ isLoading: false });
            return {
              success: false,
              error: result.error?.message || 'updateUser error',
            };
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'updateUser error';
            set({ error: errorMessage, isLoading: false });
            return {
              success: false,
              error: errorMessage,
            };
          }
        },

        refreshSession: async () => {
          try {
            const session = await authClient.getSession();

            if (session.data) {
              const user = session.data.user;
              set({
                user,
                isAuthenticated: true,
                lastUpdated: Date.now(),
              });
            } else {
              set({
                user: null,
                isAuthenticated: false,
                lastUpdated: Date.now(),
              });
            }
          } catch (error) {
            authErrorLogger.logError(error as Error, {
              operation: 'refreshSession',
            });
            set({
              user: null,
              isAuthenticated: false,
              lastUpdated: Date.now(),
            });
          }
        },

        initialize: async () => {
          if (get().isInitialized) return;

          set({ isLoading: true });
          const previousUser = get().user;

          try {
            const session = await authClient.getSession();

            if (session.data) {
              const user = session.data.user;
              // console.log('[AuthStore] Setting authenticated user:', user.email);
              const isNewUser = !previousUser || previousUser.id !== user.id;

              set({
                user,
                isAuthenticated: true,
                isLoading: false,
                isInitialized: true,
                lastUpdated: Date.now(),
              });

              // Initialize credits for new users (e.g., from social login)
              // The API will handle idempotency and prevent duplicate grants
              if (isNewUser) {
                // console.log('[AuthStore] Detected new user or switch, initializing credits...');
                await initializeUserCredits(user.id);

                // Set sign-in tracking cookie for analytics
                // Call the server helper to set the cookie
                try {
                  const response = await fetch('/api/auth/set-tracking-cookie', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ email: user.email }),
                  });
                  if (response.ok) {
                    // console.log('[AuthStore] Tracking cookie set via API');
                  }
                } catch (error) {
                  console.warn('[AuthStore] Failed to set tracking cookie:', error);
                }
              }
            } else {
              // Only clear auth if cache is invalid or expired
              if (!get().isCacheValid()) {
                set({
                  user: null,
                  isAuthenticated: false,
                  isLoading: false,
                  isInitialized: true,
                  lastUpdated: Date.now(),
                });
              } else {
                // Keep existing state if cache is valid
                set({
                  isLoading: false,
                  isInitialized: true,
                });
              }
            }
          } catch (error) {
            authErrorLogger.logError(error as Error, {
              operation: 'initialize',
            });
            // On error, keep existing state if cache is valid
            if (!get().isCacheValid()) {
              set({
                user: null,
                isAuthenticated: false,
                isLoading: false,
                isInitialized: true,
                lastUpdated: Date.now(),
              });
            } else {
              set({
                isLoading: false,
                isInitialized: true,
              });
            }
          }
        },
        clearAuth: () => {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            lastUpdated: 0,
          });
        },
      }),
      {
        name: 'better-saas-auth',
        storage: createJSONStorage(() => localStorage),
        // Only persist safe state fields
        partialize: (state) => ({
          user: state.user,
          isAuthenticated: state.isAuthenticated,
          lastUpdated: state.lastUpdated,
          cacheExpiry: state.cacheExpiry,
        }),
        skipHydration: false, // Allow automatic hydration
        version: 2,
      }
    )
  )
);

export const useUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);
export const useAuthInitialized = () => useAuthStore((state) => state.isInitialized);
export const useAuthError = () => useAuthStore((state) => state.error);
export const useInitialize = () => useAuthStore((state) => state.initialize);
export const useEmailLogin = () => useAuthStore((state) => state.signIn);
export const useClearError = () => useAuthStore((state) => state.clearError);
export const useSignInWithGoogle = () => useAuthStore((state) => state.signInWithGoogle);
export const useSignInWithLinkedIn = () => useAuthStore((state) => state.signInWithLinkedIn);
export const useSignInWithMicrosoft = () => useAuthStore((state) => state.signInWithMicrosoft);
export const useSignOut = () => useAuthStore((state) => state.signOut);
export const useRefreshSession = () => useAuthStore((state) => state.refreshSession);
export const useEmailSignup = () => useAuthStore((state) => state.signUp);
export const useSetError = () => useAuthStore((state) => state.setError);
export const useUpdateUser = () => useAuthStore((state) => state.updateUser);
export const useSetUser = () => useAuthStore((state) => state.setUser);
